/**
 * The portal title swaps "Battle Chess" <-> "Batalla Chess" once a minute
 * under a burst of fireworks.
 *
 * There is only one text layer. The old name brightens and burns out behind
 * the densest part of the burst, the text is replaced while it is invisible,
 * and the new name fades in as the embers fall — so the two names are never
 * on screen at once.
 *
 * Every frame is a pure function of time since the swap began: particles are
 * generated up front from a seed and their positions solved in closed form,
 * and the text's style is computed from the same clock. Nothing accumulates
 * between frames, which is what lets the effect be scrubbed to any instant
 * (see `render`) rather than only played.
 */

const NAMES = [
  { main: 'Battle', rest: 'Chess' },
  { main: 'Batalla', rest: 'Chess' }
];

const CYCLE_MS = 60 * 1000;
const DURATION = 2400;   // whole effect, including embers fading out
const SWAP_AT = 620;     // text content changes here, while fully faded out
const REVEAL_FROM = 760;
const REVEAL_TO = 1500;

const GRAVITY = 300;     // px/s², CSS pixels
const DRAG = 2.4;        // 1/s — velocity decays by e^(-DRAG·t)
const TRAIL = 0.055;     // seconds of path drawn behind each spark

const COLORS = ['255,246,220', '255,214,122', '255,190,90', '255,236,170', '255,255,255'];

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

function fillLayer(layer, name) {
  layer.textContent = '';
  layer.append(name.main);
  const rest = document.createElement('span');
  rest.textContent = name.rest;
  layer.append(rest);
}

/** Small seeded PRNG, so a given swap always produces the same burst. */
function mulberry32(seed) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ease = (x) => 1 - (1 - x) ** 3;
const clamp01 = (x) => Math.min(1, Math.max(0, x));

/**
 * Builds every particle for one swap. `box` is the text's extent in canvas
 * CSS pixels, so bursts land across the actual letters whatever the width.
 */
function buildParticles(rand, box) {
  const particles = [];
  const flashes = [];

  const burst = (t0, x, y, count, speedMin, speedMax, lifeMin, lifeMax, size) => {
    flashes.push({ t0, x, y, r: speedMax * 0.16 });
    for (let i = 0; i < count; i++) {
      const angle = rand() * Math.PI * 2;
      const speed = speedMin + (speedMax - speedMin) * Math.sqrt(rand());
      particles.push({
        t0,
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: lifeMin + rand() * (lifeMax - lifeMin),
        size: size * (0.6 + rand() * 0.8),
        color: COLORS[Math.floor(rand() * COLORS.length)],
        phase: rand() * Math.PI * 2
      });
    }
  };

  // The main barrage: staggered bursts marching across the old name, peaking
  // just as it burns out.
  const main = 7;
  for (let i = 0; i < main; i++) {
    const f = (i + 0.5) / main;
    burst(
      i * 62 + rand() * 30,
      box.x + box.w * (f + (rand() - 0.5) * 0.08),
      box.y + box.h * (0.3 + rand() * 0.4),
      56, 90, 430, 0.9, 1.5, 2.2
    );
  }
  // A lighter shower over the new name while it fades in.
  for (let i = 0; i < 5; i++) {
    burst(
      820 + i * 110 + rand() * 60,
      box.x + box.w * (0.1 + rand() * 0.8),
      box.y + box.h * (0.1 + rand() * 0.5),
      26, 60, 230, 0.8, 1.2, 1.7
    );
  }
  // Glitter drifting down through the whole thing.
  for (let i = 0; i < 70; i++) {
    particles.push({
      t0: 150 + rand() * 1500,
      x: box.x + box.w * rand(),
      y: box.y - box.h * 0.3 + rand() * box.h * 1.2,
      vx: (rand() - 0.5) * 40,
      vy: -20 - rand() * 40,
      life: 0.7 + rand() * 0.8,
      size: 1 + rand() * 1.2,
      color: COLORS[Math.floor(rand() * COLORS.length)],
      phase: rand() * Math.PI * 2
    });
  }
  return { particles, flashes };
}

/** Closed-form position under linear drag plus gravity (y points down). */
function positionAt(p, s) {
  const decay = (1 - Math.exp(-DRAG * s)) / DRAG;
  return [
    p.x + p.vx * decay,
    p.y + (GRAVITY / DRAG) * s + (p.vy - GRAVITY / DRAG) * decay
  ];
}

export function initPortalTitleSweep() {
  const wrap = document.getElementById('portal-title');
  if (!wrap) return null;
  const text = wrap.querySelector('.title-text');
  const canvas = wrap.querySelector('.title-fx');
  const ctx = canvas.getContext('2d');

  let index = 0;
  let swap = null;
  let raf = 0;

  fillLayer(text, NAMES[index]);

  function setTextStyle(opacity, blur, brightness, scale, glow) {
    text.style.opacity = String(opacity);
    text.style.filter = `blur(${blur}px) brightness(${brightness})`;
    text.style.transform = `scale(${scale})`;
    text.style.textShadow = glow > 0
      ? `0 0 ${18 + glow * 30}px rgba(255, 214, 122, ${0.35 + glow * 0.55})`
      : '';
  }

  function clearTextStyle() {
    text.style.opacity = '';
    text.style.filter = '';
    text.style.transform = '';
    text.style.textShadow = '';
  }

  /** The text's state at time t — the only thing the swap does to it. */
  function renderText(t) {
    const shown = t < swap.swapAt ? swap.from : swap.to;
    if (text.dataset.shown !== String(shown)) {
      fillLayer(text, NAMES[shown]);
      text.dataset.shown = String(shown);
    }

    if (swap.reduced) {
      // No particles: a plain fade out, swap, fade in. Still one layer.
      const o = t < 300 ? 1 - t / 300 : clamp01((t - 300) / 400);
      setTextStyle(o, 0, 1, 1, 0);
      return;
    }

    if (t < SWAP_AT) {
      // Ignite: flare brighter as the bursts land, then burn out.
      const heat = ease(clamp01(t / 420));
      const out = clamp01((t - 400) / (SWAP_AT - 400));
      setTextStyle(1 - out, out * 7, 1 + heat * 1.3, 1 + out * 0.05, heat);
    } else if (t < REVEAL_FROM) {
      setTextStyle(0, 8, 1.8, 0.96, 0);
    } else {
      const k = ease(clamp01((t - REVEAL_FROM) / (REVEAL_TO - REVEAL_FROM)));
      setTextStyle(k, (1 - k) * 8, 1 + (1 - k) * 0.8, 0.96 + k * 0.04, (1 - k) * 0.8);
    }
  }

  function renderSparks(t) {
    const dpr = swap.dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';

    // A soft bloom where each burst goes off.
    for (const f of swap.flashes) {
      const s = (t - f.t0) / 1000;
      if (s < 0 || s > 0.22) continue;
      const k = s / 0.22;
      const r = f.r * (0.4 + k);
      const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, r);
      g.addColorStop(0, `rgba(255,244,210,${0.55 * (1 - k)})`);
      g.addColorStop(1, 'rgba(255,190,90,0)');
      ctx.fillStyle = g;
      ctx.fillRect(f.x - r, f.y - r, r * 2, r * 2);
    }

    for (const p of swap.particles) {
      const s = (t - p.t0) / 1000;
      if (s < 0 || s > p.life) continue;
      const age = s / p.life;
      const twinkle = 0.6 + 0.4 * Math.sin(s * 38 + p.phase);
      const alpha = (1 - age) ** 1.4 * twinkle;
      const [x, y] = positionAt(p, s);
      const [tx, ty] = positionAt(p, Math.max(0, s - TRAIL));

      // Wide faint stroke for the glow, then a thin bright core.
      ctx.strokeStyle = `rgba(${p.color},${alpha * 0.25})`;
      ctx.lineWidth = p.size * 3.2;
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(x, y); ctx.stroke();
      ctx.strokeStyle = `rgba(${p.color},${alpha})`;
      ctx.lineWidth = p.size;
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(x, y); ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  /** Draws the swap exactly as it looks `t` ms after it began. */
  function render(t) {
    if (!swap) return;
    renderText(t);
    if (!swap.reduced) renderSparks(t);
  }

  /** Sets up a swap to the other name. `manual` skips the rAF loop. */
  function begin({ manual = false, seed = (Math.random() * 2 ** 32) >>> 0 } = {}) {
    const reduced = reducedMotion();
    const dpr = window.devicePixelRatio || 1;
    const cRect = canvas.getBoundingClientRect();
    const tRect = text.getBoundingClientRect();
    canvas.width = Math.round(cRect.width * dpr);
    canvas.height = Math.round(cRect.height * dpr);

    // The current name's extent. "Batalla" is only a letter wider, and the
    // bursts spray well past their origins, so it covers either name.
    const box = {
      x: tRect.left - cRect.left,
      y: tRect.top - cRect.top,
      w: tRect.width,
      h: tRect.height
    };
    const { particles, flashes } = reduced ? { particles: [], flashes: [] } : buildParticles(mulberry32(seed), box);

    swap = {
      from: index,
      to: (index + 1) % NAMES.length,
      reduced,
      dpr,
      particles,
      flashes,
      duration: reduced ? 700 : DURATION,
      // Where the text content actually changes — must land exactly where
      // that path's own opacity curve bottoms out at 0, or the name swaps
      // while still partway visible. The reduced curve (see renderText)
      // troughs at 300ms on its own much shorter 700ms timeline; SWAP_AT
      // (620ms) is tuned for the full firework timeline and is wrong here.
      swapAt: reduced ? 300 : SWAP_AT
    };
    text.dataset.shown = String(index);
    if (!manual) {
      const start = performance.now();
      const frame = (now) => {
        const t = now - start;
        if (t >= swap.duration) { finish(); return; }
        render(t);
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    }
    return swap.duration;
  }

  /** Leaves only the new name, clears the canvas and stops drawing. */
  function finish() {
    if (!swap) return;
    cancelAnimationFrame(raf);
    index = swap.to;
    fillLayer(text, NAMES[index]);
    delete text.dataset.shown;
    clearTextStyle();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    swap = null;
  }

  // Only swap where someone can see it: a burst firing behind the board or
  // in a background tab is wasted work. A skipped cycle just waits for the
  // next one.
  const visible = () => !document.hidden && !wrap.closest('[hidden]');
  setInterval(() => {
    if (!swap && visible()) begin();
  }, CYCLE_MS);

  return { begin, render, finish };
}
